import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Milestone, Project_Base_Bool_Exp, Project_Budget, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { queryFinancialsForAllYearsGivenProject } from 'src/screens/shared/project/details/financials/projectFinancialsQueries'
import { PROJECT, PROJECT_TYPE } from 'src/shared/constants/constants'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { S } from 'src/shared/styled/S'
import { DataGridUtils } from 'src/shared/utils/DataGridUtils'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { muiTheme } from 'src/theme/theme'
import { useClient } from 'urql'

interface ProjectFinancialPerYear {
  year: number
  gfchShareAnnualValue?: number | null
  shareResponsibleOrganizationAnnualValue?: number | null
  otherFundingAnnualValue?: number | null
  total?: number | null
}

interface Props {
  projectId: number
  process: PROJECT_TYPE
}

const mapMilestoneFinancialsToProjectYearlyFinancials = (milestones: Milestone[]): ProjectFinancialPerYear[] => {
  const financialReportsWithData = milestones.filter((item) => item.project_financial_report)

  const financials = financialReportsWithData.map((item) => {
    const projectFinancialReport = item.project_financial_report

    const gfchShareAnnualValue = projectFinancialReport?.gfch_share_annual_value
    const shareResponsibleOrganizationAnnualValue = projectFinancialReport?.share_responsible_organization_annual_value
    const otherFundingAnnualValue = projectFinancialReport?.other_funding_annual_value

    const allValues = [gfchShareAnnualValue, shareResponsibleOrganizationAnnualValue, otherFundingAnnualValue]
    const allValuesEmpty = allValues.every((value) => value == null)

    const financialPerYear: ProjectFinancialPerYear = {
      year: item.year_in_focus,
      gfchShareAnnualValue,
      shareResponsibleOrganizationAnnualValue,
      otherFundingAnnualValue,
      total: allValuesEmpty ? null : DataGridUtils.sum(allValues),
    }

    return financialPerYear
  })

  return financials
}

const calculateTotalActuals = (financialsPerYear: ProjectFinancialPerYear[]) => {
  const totalActuals = {
    gfchShareAnnualValues: DataGridUtils.sumBy(financialsPerYear, 'gfchShareAnnualValue'),
    shareResponsibleOrganizationAnnualValue: DataGridUtils.sumBy(
      financialsPerYear,
      'shareResponsibleOrganizationAnnualValue',
    ),
    otherAnnualValue: DataGridUtils.sumBy(financialsPerYear, 'otherFundingAnnualValue'),
    total: DataGridUtils.sumBy(financialsPerYear, 'total'),
  }
  return totalActuals
}

const getTypeOfExpenditureForEveryYear = (
  financialsPerYear: ProjectFinancialPerYear[],
  key: keyof ProjectFinancialPerYear,
): Record<number, number | undefined | null> => {
  const arrayOfFinancialsPerYear = financialsPerYear.map((item) => ({ [item.year]: item[key] }))
  return Object.assign({}, ...arrayOfFinancialsPerYear)
}

export const ProjectYearlyFinancialsGrid = ({ projectId, process }: Props): ReactElement => {
  const gridTranslations = useGridTranslateHook()
  const { getMessage } = useMessageSource()
  const apiRef = useGridApiRef()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [financialsPerYear, setFinancialsPerYear] = useState<ProjectFinancialPerYear[]>([])
  const [projectBudget, setProjectBudget] = useState<Project_Budget | null>()

  const totalApproved = useMemo(() => {
    return DataGridUtils.sum([
      projectBudget?.gfch_share_approved,
      projectBudget?.share_responsible_organization_approved,
      projectBudget?.other_funding_approved,
    ])
  }, [projectBudget])
  const totalActuals = useMemo(() => calculateTotalActuals(financialsPerYear), [financialsPerYear])

  const rowsEmpty = financialsPerYear.length === 0 ? true : false

  const fetchData = useCallback(async () => {
    const pfKapWhereClause: Project_Base_Bool_Exp = {
      pf_kap_projects: { id: { _eq: projectId } },
    }

    const pfPgvWhereClause: Project_Base_Bool_Exp = {
      pf_pgv_projects: { id: { _eq: projectId } },
    }

    const { data } = await urqlClient
      .query<{
        milestone: Query_Root['milestone']
        project_base: Query_Root['project_base']
      }>(queryFinancialsForAllYearsGivenProject, {
        projectBaseWhere: process === PROJECT.PF_KAP ? pfKapWhereClause : pfPgvWhereClause,
      })
      .toPromise()

    if (data) {
      const financialsPerYear = mapMilestoneFinancialsToProjectYearlyFinancials(data.milestone)
      setFinancialsPerYear(financialsPerYear)
      setProjectBudget(data.project_base[0].project_budget)
    } else {
      notificationService.operationFailed()
    }
  }, [urqlClient, projectId, notificationService, process])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns = useMemo<GridColDef[]>(
    () =>
      !rowsEmpty
        ? [
            {
              field: 'fundsInChf',
              headerName: getMessage('label.funds.chf'),
              flex: 0.5,
              sortable: false,
              minWidth: 200,
              type: 'string',
              renderCell({ value }) {
                return (
                  <S.DataGrid.Typography variant="subtitle2" sx={{ color: muiTheme.palette.text.disabled }}>
                    {value}
                  </S.DataGrid.Typography>
                )
              },
            },
            {
              field: 'approved',
              headerName: getMessage('label.approved'),
              flex: 0.3,
              sortable: false,
              type: 'string',
              minWidth: 80,
              renderCell: ({ value }) => {
                if (value == null || value === '' || Number.isNaN(value)) {
                  return '-'
                }
                return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
              },
            },
            ...financialsPerYear.map(
              (m) =>
                ({
                  field: `${m.year}`,
                  headerName: `${m.year}`,
                  flex: 0.3,
                  sortable: false,
                  minWidth: 80,
                  type: 'number',
                  renderCell: ({ value }) => {
                    if (value == null || value === '' || Number.isNaN(value)) {
                      return '-'
                    }
                    return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
                  },
                }) as GridColDef,
            ),
            {
              field: 'totalActuals',
              headerName: getMessage('label.total.actuals'),
              flex: 0.3,
              sortable: false,
              minWidth: 120,
              type: 'number',
              renderCell: ({ value }) => {
                if (value == null || value === '' || Number.isNaN(value)) {
                  return '-'
                }
                return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
              },
            },
          ]
        : [],
    [rowsEmpty, getMessage, financialsPerYear],
  )

  const rows = useMemo(
    () =>
      !rowsEmpty
        ? [
            {
              id: 1,
              fundsInChf: getMessage('label.gfch.share'),
              approved: projectBudget?.gfch_share_approved,
              ...getTypeOfExpenditureForEveryYear(financialsPerYear, 'gfchShareAnnualValue'),
              totalActuals: totalActuals.gfchShareAnnualValues,
            },
            {
              id: 2,
              fundsInChf: getMessage('label.share.responsible.organisation'),
              approved: projectBudget?.share_responsible_organization_approved,
              ...getTypeOfExpenditureForEveryYear(financialsPerYear, 'shareResponsibleOrganizationAnnualValue'),
              totalActuals: totalActuals.shareResponsibleOrganizationAnnualValue,
            },
            {
              id: 3,
              fundsInChf: getMessage('label.other.fundings'),
              approved: projectBudget?.other_funding_approved,
              ...getTypeOfExpenditureForEveryYear(financialsPerYear, 'otherFundingAnnualValue'),
              totalActuals: totalActuals.otherAnnualValue,
            },
            {
              id: 4,
              fundsInChf: getMessage('label.total'),
              approved: totalApproved,
              ...getTypeOfExpenditureForEveryYear(financialsPerYear, 'total'),
              totalActuals: totalActuals.total,
            },
          ]
        : [],
    [rowsEmpty, getMessage, financialsPerYear, totalActuals, projectBudget, totalApproved],
  )

  return (
    <DataGridContainer>
      <S.DataGrid.ScrollDataGridStyled
        apiRef={apiRef as any}
        rows={rows}
        columns={columns}
        initialState={{ pinnedColumns: { left: ['fundsInChf'] } }}
        localeText={gridTranslations}
        disableColumnSelector
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableMultipleColumnsSorting
        columnHeaderHeight={rowsEmpty ? 0 : undefined}
        hideFooter
      />
    </DataGridContainer>
  )
}
